import * as React from 'react';
import { useSelector } from 'react-redux';
import Alert from 'antd/lib/alert';
import Button from 'antd/lib/button';
import Empty from 'antd/lib/empty';
import Form from 'antd/lib/form';
import Modal from 'antd/lib/modal';
import Select from 'antd/lib/select';
import Space from 'antd/lib/space';
import Spin from 'antd/lib/spin';
import { AndroidDependenciesConfig } from '@/types/androidDependencies';
import { IosDependenciesConfig } from '@/types/iosDependencies';
import { Destination } from '@/types/destinations';
import { RootState } from '@/reducers/reducers';
import { selectAuthCredentials, selectWorkflowId, selectWorkflowRunId } from '@/selectors/selectors';
import { useGetDependenciesFileOptionsQuery, useSaveDependenciesConfigMutation } from '@/middlewares/defaultApi';
import {
    buildDestinationHeaderLabels,
    getDestinationId,
    isAndroidDependenciesDestination,
} from '@/components/dependencies/dependenciesUtils';
import { globalMessage } from '@/utils/globalMessage';

interface ConfigureModalValues {
    destinationId?: string;
    pbxprojPath: string;
    packageResolvedPath: string;
    xcconfigPaths?: string[];
    buildGradlePaths: string[];
    gradleLockfilePaths?: string[];
    versionCatalogPaths?: string[];
    gradlePropertiesPaths?: string[];
    buildSrcPaths?: string[];
}

// Destinations are often unnamed, so share the labelling the panel headers use: the platform's
// store name, disambiguated by id when two would read the same.
const destinationOptions = (destinations: Destination[]) => {
    const labels = buildDestinationHeaderLabels(
        destinations.map((destination) => ({
            destinationId: getDestinationId(destination),
            destinationName: destination.name,
            platform: destination.platform,
            appStoreType: destination.appStoreType,
        })),
    );

    return destinations.map((destination) => {
        const destinationId = getDestinationId(destination);
        return { label: labels[destinationId] || destinationId, value: destinationId };
    });
};

const pathOptions = (paths: string[]) => paths.map((path) => ({ label: path, value: path }));

const ConfigureDependenciesModal: React.FC<{
    open: boolean;
    orgId: string;
    appId: string;
    releaseId: string;
    destinations: Destination[];
    initialDestinationId?: string;
    existingIosConfigs: Record<string, IosDependenciesConfig>;
    existingAndroidConfigs: Record<string, AndroidDependenciesConfig>;
    onConfigured: () => void;
    onClose: () => void;
}> = ({
    open,
    orgId,
    appId,
    releaseId,
    destinations,
    initialDestinationId,
    existingIosConfigs,
    existingAndroidConfigs,
    onConfigured,
    onClose,
}) => {
    const [form] = Form.useForm<ConfigureModalValues>();
    const auth = useSelector((state: RootState) => selectAuthCredentials(state));
    const [isSaving, setIsSaving] = React.useState(false);
    const selectedDestinationId = Form.useWatch('destinationId', form);

    const defaultDestinationId =
        initialDestinationId ?? (destinations.length === 1 ? getDestinationId(destinations[0]) : undefined);
    const destinationId = selectedDestinationId ?? defaultDestinationId;
    const destination = destinations.find((d) => getDestinationId(d) === destinationId);
    const isAndroid = !!destination && isAndroidDependenciesDestination(destination);

    const workflowId = useSelector((state: RootState) => selectWorkflowId(state, appId));
    const workflowRunId = useSelector((state: RootState) => selectWorkflowRunId(state, releaseId));
    const { data: fileOptionsResponse, isFetching: isLoading } = useGetDependenciesFileOptionsQuery(
        { orgId, workflowId: workflowId ?? '', workflowRunId, destinationId: destinationId ?? '' },
        { skip: !open || !auth || !workflowId || !workflowRunId || !destinationId },
    );
    const [saveDependenciesConfig] = useSaveDependenciesConfigMutation();

    const fileOptions = fileOptionsResponse?.data;
    const iosFileOptions = fileOptions?.platform === 'ios' ? fileOptions.ios : undefined;
    const androidFileOptions = fileOptions?.platform === 'android' ? fileOptions.android : undefined;

    React.useEffect(() => {
        if (!open) {
            return;
        }
        const existingIos = destinationId ? existingIosConfigs[destinationId] : undefined;
        const existingAndroid = destinationId ? existingAndroidConfigs[destinationId] : undefined;
        form.setFieldsValue({
            destinationId,
            pbxprojPath: existingIos?.pbxprojPath,
            packageResolvedPath: existingIos?.packageResolvedPath,
            xcconfigPaths: existingIos?.xcconfigPaths ?? [],
            buildGradlePaths: existingAndroid?.buildGradlePaths ?? [],
            gradleLockfilePaths: existingAndroid?.gradleLockfilePaths ?? [],
            versionCatalogPaths: existingAndroid?.versionCatalogPaths ?? [],
            gradlePropertiesPaths: existingAndroid?.gradlePropertiesPaths ?? [],
            buildSrcPaths: existingAndroid?.buildSrcPaths ?? [],
        });
    }, [open, destinationId, existingIosConfigs, existingAndroidConfigs, form]);

    const clearAllAndroidPaths = () =>
        form.setFieldsValue({
            buildGradlePaths: [],
            gradleLockfilePaths: [],
            versionCatalogPaths: [],
            gradlePropertiesPaths: [],
            buildSrcPaths: [],
        });

    type AndroidPathFieldName =
        | 'buildGradlePaths'
        | 'gradleLockfilePaths'
        | 'versionCatalogPaths'
        | 'gradlePropertiesPaths'
        | 'buildSrcPaths';

    // Selecting a repo's worth of paths one by one is the slow part of this form, so every list
    // offers to take all of them, and the collapsed tags keep a big selection readable.
    const androidPathField = ({
        label,
        name,
        paths,
        placeholder,
        extra,
        rules,
    }: {
        label: string;
        name: AndroidPathFieldName;
        paths: string[];
        placeholder: string;
        extra?: string;
        rules?: React.ComponentProps<typeof Form.Item>['rules'];
    }) => (
        <React.Fragment key={name}>
            <Form.Item label={label} name={name} extra={extra} rules={rules} style={{ marginBottom: 4 }}>
                <Select
                    mode="multiple"
                    showSearch
                    allowClear
                    maxTagCount="responsive"
                    disabled={isLoading}
                    options={pathOptions(paths)}
                    placeholder={placeholder}
                />
            </Form.Item>
            <Button
                type="link"
                size="small"
                style={{ padding: 0, marginBottom: 16 }}
                disabled={isLoading || paths.length === 0}
                onClick={() => form.setFieldsValue({ [name]: paths })}
            >
                {paths.length > 0 ? `Select all ${paths.length}` : 'None found'}
            </Button>
        </React.Fragment>
    );

    const handleOk = () => {
        form.validateFields().then(async (values) => {
            const chosenDestinationId = values.destinationId ?? defaultDestinationId;
            if (!auth || !chosenDestinationId || !workflowId || !workflowRunId) {
                return;
            }

            setIsSaving(true);
            try {
                await saveDependenciesConfig({
                    orgId,
                    workflowId,
                    workflowRunId,
                    destinationId: chosenDestinationId,
                    android:
                        isAndroid ?
                            {
                                buildGradlePaths: values.buildGradlePaths ?? [],
                                gradleLockfilePaths: values.gradleLockfilePaths ?? [],
                                versionCatalogPaths: values.versionCatalogPaths ?? [],
                                gradlePropertiesPaths: values.gradlePropertiesPaths ?? [],
                                buildSrcPaths: values.buildSrcPaths ?? [],
                            }
                        :   undefined,
                    ios:
                        isAndroid ? undefined : (
                            {
                                pbxprojPath: values.pbxprojPath,
                                packageResolvedPath: values.packageResolvedPath,
                                xcconfigPaths: values.xcconfigPaths ?? [],
                                shouldIncludeReposOwnedByCustomer: false,
                            }
                        ),
                }).unwrap();

                globalMessage.success('Dependency configuration saved');
                onConfigured();
                onClose();
            } catch (e) {
                globalMessage.error(e instanceof Error ? e.message : 'Error saving dependency configuration');
            } finally {
                setIsSaving(false);
            }
        });
    };

    // Which fields to show depends entirely on the chosen destination's platform, so until one is
    // chosen there is nothing correct to render.
    const hasDestination = !!destination;
    const branch = isAndroid ? androidFileOptions?.branch : iosFileOptions?.branch;
    const loadedFileOptions = isAndroid ? androidFileOptions : iosFileOptions;
    // The required file is the one the scan cannot run without, so its absence means this branch
    // holds nothing we can read — worth saying plainly rather than showing empty pickers.
    const requiredPaths = isAndroid ? androidFileOptions?.buildGradlePaths : iosFileOptions?.packageResolvedPaths;
    const hasNoFiles = hasDestination && !isLoading && !!loadedFileOptions && (requiredPaths ?? []).length === 0;
    const noFilesMessage =
        isAndroid ?
            `No build.gradle or build.gradle.kts files were found on ${branch}. Runway reads dependencies from the repository, so there is nothing to configure for this destination.`
        :   `No Package.resolved file was found on ${branch}. Runway reads dependencies from the repository, so there is nothing to configure for this destination.`;

    return (
        <Modal
            open={open}
            title="Configure dependencies"
            okText="Save configuration"
            onOk={handleOk}
            onCancel={onClose}
            confirmLoading={isSaving}
            okButtonProps={{ disabled: hasNoFiles || !hasDestination }}
            width={720}
        >
            <Space direction="vertical" size={16} style={{ width: '100%' }}>
                {!hasDestination ?
                    null
                : hasNoFiles ?
                    <Alert
                        type="warning"
                        showIcon
                        message="No dependency files on this branch"
                        description={noFilesMessage}
                    />
                : branch ?
                    <Alert type="info" message={`Showing files from ${branch}`} />
                :   null}
                <Spin spinning={isLoading} tip="Loading files from the release branch…">
                    <Form form={form} layout="vertical">
                        {destinations.length > 1 && (
                            <Form.Item
                                label="Destination"
                                name="destinationId"
                                rules={[{ required: true, message: 'Choose a destination' }]}
                            >
                                <Select
                                    disabled={!!initialDestinationId}
                                    options={destinationOptions(destinations)}
                                    placeholder="Choose destination"
                                />
                            </Form.Item>
                        )}
                        {!hasDestination ?
                            <Empty
                                image={Empty.PRESENTED_IMAGE_SIMPLE}
                                description="Select a destination to configure its dependencies"
                            />
                        : isAndroid ?
                            <>
                                <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                                    <Button
                                        type="link"
                                        size="small"
                                        style={{ padding: 0 }}
                                        disabled={isLoading}
                                        onClick={clearAllAndroidPaths}
                                    >
                                        Clear all
                                    </Button>
                                </div>
                                {androidPathField({
                                    label: 'build.gradle files',
                                    name: 'buildGradlePaths',
                                    paths: androidFileOptions?.buildGradlePaths ?? [],
                                    placeholder: 'Choose build.gradle files',
                                    extra: 'Pick one per Gradle module you want covered.',
                                    rules: [{ required: true, message: 'Choose at least one build.gradle file' }],
                                })}
                                {androidPathField({
                                    label: 'Gradle lockfiles',
                                    name: 'gradleLockfilePaths',
                                    paths: androidFileOptions?.gradleLockfilePaths ?? [],
                                    placeholder: 'Choose Gradle lockfiles',
                                })}
                                {androidPathField({
                                    label: 'Version catalogs',
                                    name: 'versionCatalogPaths',
                                    paths: androidFileOptions?.versionCatalogPaths ?? [],
                                    placeholder: 'Choose libs.versions.toml files',
                                    extra: 'Needed to resolve dependencies declared as libs.* accessors.',
                                })}
                                {androidPathField({
                                    label: 'gradle.properties',
                                    name: 'gradlePropertiesPaths',
                                    paths: androidFileOptions?.gradlePropertiesPaths ?? [],
                                    placeholder: 'Choose gradle.properties files',
                                    extra: 'Needed to resolve versions referenced as $property in build files.',
                                })}
                                {androidPathField({
                                    label: 'buildSrc files',
                                    name: 'buildSrcPaths',
                                    paths: androidFileOptions?.buildSrcPaths ?? [],
                                    placeholder: 'Choose buildSrc Kotlin files',
                                    extra: 'Needed for versions and coordinates kept in buildSrc Kotlin objects.',
                                })}
                                <Alert
                                    type="info"
                                    showIcon
                                    message="Without a Gradle lockfile only directly declared dependencies can be listed. Lockfiles require dependency locking to be enabled in the build."
                                />
                            </>
                        :   <>
                                <Form.Item
                                    label="project.pbxproj"
                                    name="pbxprojPath"
                                    rules={[{ required: true, message: 'Choose a pbxproj file' }]}
                                >
                                    <Select
                                        showSearch
                                        disabled={isLoading}
                                        options={pathOptions(iosFileOptions?.pbxprojPaths ?? [])}
                                        placeholder="Choose project.pbxproj"
                                    />
                                </Form.Item>
                                <Form.Item
                                    label="Package.resolved"
                                    name="packageResolvedPath"
                                    rules={[{ required: true, message: 'Choose a Package.resolved file' }]}
                                >
                                    <Select
                                        showSearch
                                        disabled={isLoading}
                                        options={pathOptions(iosFileOptions?.packageResolvedPaths ?? [])}
                                        placeholder="Choose Package.resolved"
                                    />
                                </Form.Item>
                                <Form.Item label=".xcconfig files" name="xcconfigPaths">
                                    <Select
                                        mode="multiple"
                                        showSearch
                                        disabled={isLoading}
                                        options={pathOptions(iosFileOptions?.xcconfigPaths ?? [])}
                                        placeholder="Choose xcconfig files"
                                    />
                                </Form.Item>
                                {!!iosFileOptions?.xcconfigPaths?.length && (
                                    <Button
                                        type="link"
                                        style={{ padding: 0 }}
                                        onClick={() =>
                                            form.setFieldsValue({ xcconfigPaths: iosFileOptions.xcconfigPaths })
                                        }
                                    >
                                        Select all xcconfig files
                                    </Button>
                                )}
                            </>
                        }
                    </Form>
                </Spin>
            </Space>
        </Modal>
    );
};

export default ConfigureDependenciesModal;
